<template>
  <el-config-provider :locale="elmLocale">
    <router-view />
    <HelpArticleDialog />
    <FeedbackDrawer />
  </el-config-provider>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTagsViewStore } from '@/pinia/stores/tags-view'
import { useUserStore } from '@/pinia/stores/user'
import { useLocaleStore } from '@/pinia/stores/locale'
import { websocketService } from '@/common/services/websocket'
import { ElMessage, ElNotification } from 'element-plus'
import { useI18n } from 'vue-i18n'
import { clearFeedbackContext } from '@/common/composables/useFeedbackContext'
import { emitFeedbackRealtime } from '@/common/composables/useFeedbackRealtime'
import HelpArticleDialog from '@/components/help/HelpArticleDialog.vue'
import FeedbackDrawer from '@/components/feedback/FeedbackDrawer.vue'

const { t } = useI18n()
const route = useRoute()
const tagsViewStore = useTagsViewStore()
const userStore = useUserStore()
const localeStore = useLocaleStore()
const { elmLocale } = storeToRefs(localeStore)

/** 后端已按 userId 定向推送，此处直接静默合并 */
const onUserProfileUpdate = (payload: Record<string, unknown> | null | undefined) => {
  userStore.patchUserInfo(payload)
}

/** 团队积分变更：该团队在线成员都会收到，更新侧栏加总与创作页余额 */
const onTeamPointUpdate = (payload: Record<string, unknown> | null | undefined) => {
  userStore.applyTeamPointUpdate(payload)
}

// 监听任务状态更新
onMounted(() => {
  websocketService.on('task-update', (data) => {
    console.log('任务状态更新:', data)
    // 更新任务列表
    // refreshTaskList()
  })

  // 监听视频生成完成
  websocketService.on('video-generated', (data) => {
    ElNotification({
      title: '视频生成完成',
      message: `任务${data.taskId}已完成`,
      type: 'success'
    })
  })

  websocketService.on('user-profile-update', onUserProfileUpdate)
  websocketService.on('team-point-update', onTeamPointUpdate)

  websocketService.on('feedback-update', (data) => {
    // 已读游标推进：只驱动抽屉/客服台刷新，不弹 Toast
    if (data?.event === 'FEEDBACK_READ') {
      emitFeedbackRealtime(data || {})
      return
    }
    const ticketNo = data?.ticketNo ? `#${data.ticketNo}` : ''
    let message = t('feedback.notify.updated', { ticketNo })
    if (data?.event === 'FEEDBACK_CREATED') {
      message = t('feedback.notify.created', { ticketNo })
    } else if (data?.event === 'FEEDBACK_REPLY') {
      message = data?.senderType === 'STAFF'
        ? t('feedback.notify.staffReply', { ticketNo })
        : t('feedback.notify.userReply', { ticketNo })
    } else if (data?.event === 'FEEDBACK_STATUS_CHANGED') {
      message = t('feedback.notify.statusChanged', { ticketNo })
    }
    ElMessage({
      message,
      type: 'info',
      duration: 3500,
      showClose: true,
    })
    emitFeedbackRealtime(data || {})
  })
})

onUnmounted(() => {
  websocketService.off('task-update')
  websocketService.off('video-generated')
  websocketService.off('user-profile-update', onUserProfileUpdate)
  websocketService.off('team-point-update', onTeamPointUpdate)
  websocketService.off('feedback-update')
})

// 添加标签页；路由切换时清掉页面登记的业务上下文，避免跨页残留
watch(() => route.fullPath, () => {
  clearFeedbackContext()
  const currentPath = route.path || ''
  const isLoginRoute = currentPath === '/login'
  const isHiddenRoute = Boolean(route.meta?.hidden)

  // 清理已存在的登录标签，避免登录后仍然残留
  if (!isLoginRoute) {
    const loginTag = tagsViewStore.visitedViews.find(tag => tag.path === '/login')
    if (loginTag) {
      tagsViewStore.delView(loginTag)
      tagsViewStore.delCachedView(loginTag)
    }
  }

  // 登录页不进入标签页
  if (!route.name || isLoginRoute) {
    return
  }

  // 隐藏路由通常不进入标签页，但视频生成页面除外（它虽然在菜单中隐藏，但需要作为标签页展示）
  if (isHiddenRoute && currentPath !== '/video-generation') {
    return
  }

  tagsViewStore.addView(route)
  tagsViewStore.addCachedView(route)
}, { immediate: true })
</script>

<style>
#app {
  width: 100%;
  height: 100%;
}
</style>

